import { Link } from 'react-router-dom';
import { COMPANY, LegalDoc, ToConfirm, type LegalSection } from '../components/LegalDoc';

/**
 * How to delete a NyumbaSuite account, at an address anyone can open.
 *
 * Google Play asks every app that lets people create an account for two ways
 * out: one inside the app, and a web page that works for someone who has
 * already uninstalled it. This is the web page; its address goes into the Play
 * Console's Data safety form. It must stay public (no sign-in), and it must say
 * plainly what is removed and what is kept, because that is what the reviewer
 * checks it against.
 *
 * Everything below describes what the server does today (src/domain/profile.ts:
 * a 30-day grace period, then the login is closed and every phone signed out).
 * The erasure period is marked TO BE CONFIRMED until the retention policy is
 * settled, the same way the privacy policy marks its open facts.
 */

const sections: LegalSection[] = [
  {
    id: 'in-the-app',
    heading: 'From the NyumbaSuite app',
    body: (
      <ol>
        <li>Open the app and sign in.</li>
        <li>
          Open <strong>Profile &amp; security</strong>: it is under <strong>More</strong>,
          or behind the person icon at the top right of the first screen.
        </li>
        <li>Scroll to the bottom and tap <strong>Delete my account</strong>.</li>
        <li>Confirm with <strong>Delete in 30 days</strong>. You are signed out straight away.</li>
      </ol>
    ),
  },
  {
    id: 'without-the-app',
    heading: 'Without the app',
    body: (
      <>
        <p>
          If you no longer have the app, email{' '}
          <a href={`mailto:${COMPANY.privacyEmail}?subject=Delete%20my%20NyumbaSuite%20account`}>
            {COMPANY.privacyEmail}
          </a>{' '}
          from the email address you sign in with, with the subject line
          "Delete my NyumbaSuite account". Include the phone number on the account so we
          can match it. We confirm by reply, usually within two working days, and the
          same 30-day period then applies.
        </p>
        <p>
          We will never ask for your password to do this.
        </p>
      </>
    ),
  },
  {
    id: 'what-happens',
    heading: 'What happens next',
    body: (
      <>
        <p>
          For 30 days nothing is removed. If you change your mind, just sign in again
          and the request is cancelled.
        </p>
        <p>After 30 days:</p>
        <ul>
          <li>your login is closed and can no longer be used;</li>
          <li>every phone signed in to the account is signed out and stops receiving notifications;</li>
          <li>
            if you own a landlord or agency account, the whole account is closed, its
            subscription is cancelled and every staff, owner and tenant login in it is
            closed too;
          </li>
          <li>
            your name, email address and phone number are erased within{' '}
            <ToConfirm>the period TO BE CONFIRMED</ToConfirm>, apart from what is listed below.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'what-is-kept',
    heading: 'What is kept, and why',
    body: (
      <>
        <p>
          <strong>If you are a tenant</strong>, your landlord's own records of your
          tenancy (rent charged, payments received, receipts, repairs) belong to the
          landlord, not to NyumbaSuite, and stay with them. Deleting your login does not
          remove them. To have those corrected or removed, ask your landlord, or write to
          us and we will put you in touch.
        </p>
        <p>
          <strong>If you own the account</strong>, financial records such as invoices,
          receipts and M-Pesa payment references are kept for as long as Kenyan tax law
          requires, <ToConfirm>currently understood to be five years</ToConfirm>, and
          then deleted. The audit log of who did what is kept for the same period.
        </p>
        <p>
          Before you delete a landlord account you can download everything you recorded
          as spreadsheets from <Link to="/data">Import &amp; export</Link>. The full
          detail is in the <Link to="/privacy#how-long">Privacy Policy</Link>.
        </p>
      </>
    ),
  },
];

export function DeleteAccount() {
  return (
    <LegalDoc
      title="Delete your account"
      lede="How to close your NyumbaSuite account and remove your personal data, with or without the app."
      effectiveDate="27 September 2026"
      canonicalPath="/delete-account"
      draft
      sections={sections}
    />
  );
}
